import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Bell, BellRing, Trash2, Send, CheckCheck, Phone, Mail, Package, MessageCircle } from "lucide-react";
import { fmtDateTime } from "../../lib/format";
import { errText } from "../../lib/errors";

const waNumber = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.startsWith("90")) return d;
  if (d.startsWith("0")) return `9${d}`;
  return `90${d}`;
};

const waNotify = (a) => {
  const url = `${window.location.origin}/urun/${a.product_slug || a.product_id}`;
  const msg = `Merhaba, Ata Angora Doğal Gıdalar'dan yazıyoruz. Bildirim istediğiniz "${a.product_name || "ürün"}" stoğa girdi: ${url}`;
  window.open(`https://wa.me/${waNumber(a.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
};

const TABS = [["bekliyor", "Bekleyen"], ["hazir", "Stoğa Girdi"], ["bildirildi", "Bildirildi"], ["", "Tümü"]];

const STATUS_BADGE = {
  bekliyor: "bg-amber-100 text-amber-800",
  hazir: "bg-emerald-100 text-emerald-800",
  bildirildi: "bg-stone-100 text-stone-600",
};
const STATUS_LABEL = { bekliyor: "Bekliyor", hazir: "Stoğa Girdi", bildirildi: "Bildirildi" };

export default function AdminStockAlerts() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("bekliyor");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/stock-alerts", { params: tab ? { status: tab } : {} }).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const notify = async (a) => {
    setBusy(a.alert_id);
    try {
      await api.post(`/admin/stock-alerts/${a.alert_id}/notify`);
      toast.success(`${a.email} adresine bildirim e-postası gönderildi`);
      load();
    } catch (e) { toast.error(errText(e) || "Gönderilemedi"); }
    finally { setBusy(""); }
  };

  const markNotified = async (a) => {
    try {
      await api.post(`/admin/stock-alerts/${a.alert_id}/notified`);
      toast.success("Bildirildi olarak işaretlendi");
      load();
    } catch { toast.error("İşlenemedi"); }
  };

  const whatsapp = async (a) => {
    waNotify(a);
    await markNotified(a);
  };

  const del = async (a) => {
    if (!window.confirm("Bu bildirim kaydı kalıcı olarak silinsin mi?")) return;
    try {
      await api.delete(`/admin/stock-alerts/${a.alert_id}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  return (
    <div data-testid="admin-stock-alerts-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl sm:text-3xl text-forest">Stok Bildirimleri</h1>
        <p className="text-stone-500 text-sm mt-1">"Stok Gelince Haber Ver" isteyen müşteriler. Ürün stoğa girince otomatik e-posta gider; buradan manuel de bildirebilirsiniz.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`sa-tab-${k || "all"}`} onClick={() => setTab(k)} className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors ${tab === k ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{label}</button>
        ))}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="bg-white rounded-2xl border border-stone-200/80 p-10 text-center text-stone-400 text-sm"><Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />Bu durumda bildirim isteği yok</div>}
        {items.map((a) => (
          <div key={a.alert_id} data-testid={`sa-row-${a.alert_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/urun/${a.product_slug || a.product_id}`} className="font-semibold text-sm text-forest hover:underline flex items-center gap-1.5"><Package className="w-4 h-4" />{a.product_name || a.product_id}</Link>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${STATUS_BADGE[a.status] || "bg-stone-100 text-stone-600"}`}>{STATUS_LABEL[a.status] || a.status}</span>
                </div>
                <div className="mt-2 space-y-1 text-sm text-stone-600">
                  {a.email && <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-stone-400" />{a.email}</p>}
                  {a.phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-stone-400" />{a.phone}</p>}
                  <p className="text-xs text-stone-400">İstek: {fmtDateTime(a.created_at)}{a.notified_at ? ` · Bildirim: ${fmtDateTime(a.notified_at)}` : ""}</p>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {a.status !== "bildirildi" && a.email && (
                  <button data-testid={`sa-notify-${a.alert_id}`} onClick={() => notify(a)} disabled={busy === a.alert_id} className="flex items-center gap-1.5 text-xs font-semibold bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white px-3 py-2 rounded-lg transition-colors"><Send className="w-3.5 h-3.5" /> {busy === a.alert_id ? "Gönderiliyor..." : "E-posta Gönder"}</button>
                )}
                {a.status !== "bildirildi" && a.phone && (
                  <button data-testid={`sa-wa-${a.alert_id}`} onClick={() => whatsapp(a)} className="flex items-center gap-1.5 text-xs font-semibold bg-[#25D366] hover:bg-[#1EBE5D] text-white px-3 py-2 rounded-lg transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
                )}
                {a.status !== "bildirildi" && !a.email && !a.phone && (
                  <button data-testid={`sa-mark-${a.alert_id}`} onClick={() => markNotified(a)} className="flex items-center gap-1.5 text-xs font-semibold bg-gold hover:bg-gold-dark text-stone-900 px-3 py-2 rounded-lg transition-colors"><CheckCheck className="w-3.5 h-3.5" /> Bildirildi İşaretle</button>
                )}
                {a.status === "bildirildi" && <span className="flex items-center gap-1 text-xs text-stone-400 px-2"><BellRing className="w-4 h-4" /> Tamamlandı</span>}
                <button data-testid={`sa-del-${a.alert_id}`} onClick={() => del(a)} className="p-2 border border-red-200 hover:bg-red-50 rounded-lg text-terra" title="Sil"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
